// MarketerConsultantHistoryModal.jsx — 마케터 1:1 '변경 기록' 창 (2026-10-06 UX 5단계에서 다시 짬)
//   회사 전체 기록 또는 배정 하나의 기록. 종류 거르기([전체][배정][급여 조건][넣기·빼기][확정][상담자배분]) + 이름 찾기.
//   한 줄 = 이름표 · 대상 · 누가 · 언제 + 바뀐 내용(전 → 후). 공용 창(휴대폰은 아래에서 올라오는 시트).
import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { HISTORY_GROUPS, describeLog, filterLogs } from './historyText';

const H = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: (active) => ({
    minHeight: 40, borderRadius: 999, padding: '0 12px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
    border: active ? '1px solid #2563eb' : '1px solid var(--border)', background: active ? 'rgba(37,99,235,0.10)' : 'var(--bg-card)',
    color: active ? '#2563eb' : 'var(--text-sub)',
  }),
  search: {
    width: '100%', height: 40, borderRadius: 10, border: '1px solid var(--border)', padding: '0 12px', fontSize: 14,
    background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
  },
  item: (tone) => ({
    borderLeft: `3px solid ${tone.bar}`, borderRadius: '0 10px 10px 0', background: 'var(--bg-subtle)', padding: '10px 12px',
    display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
  }),
  top: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  badge: (tone) => ({ fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: tone.bg, color: tone.fg }),
  target: { fontSize: 13, fontWeight: 800, color: 'var(--text)', minWidth: 0, overflowWrap: 'anywhere' },
  meta: { marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  line: { display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-sub)', flexWrap: 'wrap', alignItems: 'baseline' },
  lineLabel: { fontWeight: 800, minWidth: 84 },
  before: { color: '#dc2626', textDecoration: 'line-through', opacity: 0.8 },
  after: { color: 'var(--text)', fontWeight: 700, overflowWrap: 'anywhere' },
  empty: { padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' },
  count: { fontSize: 12, color: 'var(--text-muted)' },
};

function Entry({ item }) {
  return (
    <div style={H.item(item.tone)}>
      <div style={H.top}>
        <span style={H.badge(item.tone)}>{item.label}</span>
        {item.target && <span style={H.target}>{item.target}</span>}
        <span style={H.meta}>{item.who} · {item.when}</span>
      </div>
      {item.lines.map((line, index) => (
        <div key={`${line.label}-${index}`} style={H.line}>
          <span style={H.lineLabel}>{line.label}</span>
          {line.before !== undefined && <span style={H.before}>{line.before || '-'}</span>}
          {line.before !== undefined && <span>→</span>}
          <span style={H.after}>{line.after || '-'}</span>
        </div>
      ))}
    </div>
  );
}

export function MarketerConsultantHistoryModal({ history, companyName }) {
  const [group, setGroup] = useState('all');
  const [keyword, setKeyword] = useState('');
  const described = useMemo(() => (history.logs || []).map((log) => ({ id: log.id, ...describeLog(log) })), [history.logs]);
  if (!history.open) return null;

  const forOne = history.scope === 'assignment';
  // 배정 하나 기록은 거르기 칸이 없으니 걸러내지 않는다
  const list = forOne ? described : filterLogs(described, group, keyword);
  const title = forOne
    ? `${history.row?.consultant_name || '배정'} 변경 기록`
    : `변경 기록 · ${companyName || ''}`;

  return (
    <Modal open title={title} icon="🕘" onClose={history.close} width="min(780px, calc(100vw - 48px))" mobileSheet mobileSheetDragClose>
      <div style={H.wrap}>
        {!forOne && (
          <>
            <div style={H.chips}>
              {HISTORY_GROUPS.map((item) => (
                <button key={item.key} type="button" style={H.chip(group === item.key)} onClick={() => setGroup(item.key)}>{item.label}</button>
              ))}
            </div>
            <input
              type="search"
              style={H.search}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="이름으로 찾기"
              aria-label="변경 기록에서 이름 찾기"
            />
          </>
        )}
        {forOne && history.row && (
          <div style={H.count}>담당 {history.row.marketer_name || '-'} · 이 배정에서 바뀐 것만 보여요(급여 조건·넣기/빼기 포함).</div>
        )}
        {history.loading && <div style={H.empty}>불러오는 중입니다.</div>}
        {!history.loading && history.error && <div style={{ ...H.empty, color: '#dc2626' }}>{history.error}</div>}
        {!history.loading && !history.error && list.length === 0 && (
          <div style={H.empty}>{described.length ? '이 조건에 맞는 기록이 없어요.' : '변경 기록이 없습니다.'}</div>
        )}
        {!history.loading && list.length > 0 && (
          <>
            <div style={H.count}>{list.length.toLocaleString('ko-KR')}건 · 최근 것부터</div>
            {list.map((item) => <Entry key={item.id} item={item} />)}
          </>
        )}
      </div>
    </Modal>
  );
}
